import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Mail, ChevronLeft, Send, Loader2, CheckCircle2 } from 'lucide-react';
import AuthLayout from '../components/common/AuthLayout';

const ForgotPasswordPage = () => {
    const [email, setEmail] = useState('');
    const [error, setError] = useState('');
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const { resetPassword } = useAuth();
    const navigate = useNavigate();

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setIsLoading(true);

        try {
            await resetPassword(email);
            setIsSubmitted(true);
        } catch (err) {
            setError(err.message || 'Failed to send reset link. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    if (isSubmitted) {
        return (
            <AuthLayout>
                <div className="flex flex-col items-center gap-4 text-center">
                    <div className="p-4 bg-emerald-50 dark:bg-emerald-900/20 rounded-full text-emerald-600 dark:text-emerald-400">
                        <CheckCircle2 size={48} />
                    </div>
                    <h2 className="text-3xl font-black text-gray-900 dark:text-white tracking-tight">Check your email</h2>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                        We've sent a password reset link to <span className="text-gray-900 dark:text-white font-bold">{email}</span>
                    </p>
                </div>
                <button
                    onClick={() => navigate('/login')}
                    className="mt-8 w-full flex justify-center items-center gap-2 py-4 px-6 text-sm font-bold rounded-xl text-white bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 transition-all duration-200 shadow-lg shadow-emerald-500/30"
                >
                    Back to Sign In
                </button>
            </AuthLayout>
        );
    }

    return (
        <AuthLayout>
            <Link
                to="/login"
                className="inline-flex items-center gap-2 text-sm font-bold text-gray-500 hover:text-emerald-600 dark:hover:text-emerald-400 transition-colors group mb-6"
            >
                <ChevronLeft size={16} className="group-hover:-translate-x-0.5 transition-transform" />
                Back to Sign In
            </Link>

            <div className="space-y-2">
                <h2 className="text-3xl font-black text-gray-900 dark:text-white tracking-tight">Reset password</h2>
                <p className="text-sm text-gray-600 dark:text-gray-400">Enter your email and we'll send you a link</p>
            </div>

            <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
                {error && (
                    <div className="bg-red-50/80 dark:bg-red-950/40 backdrop-blur-sm border border-red-200 dark:border-red-900/50 text-red-700 dark:text-red-400 text-sm font-semibold p-4 rounded-xl">
                        {error}
                    </div>
                )}

                <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400">
                        <Mail size={18} />
                    </div>
                    <input
                        type="email"
                        required
                        className="block w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:border-emerald-500 dark:focus:border-emerald-400 transition-colors"
                        placeholder="Email address"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                    />
                </div>

                <button
                    type="submit"
                    disabled={isLoading}
                    className="group relative w-full flex justify-center items-center gap-2 py-4 px-6 text-sm font-bold rounded-xl text-white bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 focus:outline-none transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed shadow-lg shadow-emerald-500/30"
                >
                    {isLoading ? (
                        <>
                            <Loader2 className="animate-spin" size={20} />
                            <span>Sending...</span>
                        </>
                    ) : (
                        <>
                            <Send size={18} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                            <span>Send Reset Link</span>
                        </>
                    )}
                </button>
            </form>
        </AuthLayout>
    );
};

export default ForgotPasswordPage;
